import { Controller, useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { Button, Flex, Form, Input, Select, Skeleton } from 'antd'
import { menuUrls } from 'src/app/shared/constants/menu.constants'
import Expirations from './Expirations'
import NavigationOptions from './NavigationOptions'
import Switchers from './Switchers'
import { useDispatch, useSelector } from 'react-redux'
import {
  getEmailsSetup,
  getModulesSetup,
  getPermissionsSetup,
  getPrimaryGroupSetup,
  setUpEmails,
  setUpModules,
  setUpPermissions,
  setUpPrimaryGroupTypes,
} from 'src/app/store/userRoleSetup'
import { useEffect, useMemo, useState } from 'react'
import {
  fetchPermission,
  fetchPrimaryGroupType,
  fetchSubModuleItem,
  fetchUserNotificationType,
  getUserRoleById,
} from 'src/app/shared/services/UserRoleService'
import { EXPIRATION_TYPE, DEFAULT_GROUP_TYPE, DEFAULT_VIEW_ITEMS } from './constants'
import { getActiveAll } from 'src/app/shared/utils'
import { SUPER_ADMIN_ONLY_PERMISSIONS } from 'src/app/shared/models/roleAndPermission.model'
import { UserRoleIds } from 'src/app/shared/constants/app.constants'

const ViewUserRole = () => {
  const params = useParams()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const primaryGroupTypes = useSelector(getPrimaryGroupSetup)
  const [defaultGroupType, setDefaultGroupType] = useState<string>('')
  const [defaultPermission, setDefaultPermission] = useState<string>('')
  const permissions = useSelector(getPermissionsSetup)
  const emails = useSelector(getEmailsSetup)
  const modules = useSelector(getModulesSetup)
  const { id } = params
  const [isFirstTimeLoadFinished, setIsFirstTimeLoadFinished] = useState<boolean>(false)

  const isSuperAdminRole = id?.toLowerCase() === UserRoleIds.SUPER_ADMIN.toLowerCase()
  const filteredPermissions = isSuperAdminRole
    ? permissions
    : permissions?.filter((permission: any) => !SUPER_ADMIN_ONLY_PERMISSIONS.includes(permission?.name))

  const defaultValues = useMemo(() => {
    return {
      primaryGroupType: defaultGroupType,
      roleName: '',
      selectAllNavigations: false,
      selectAllPermissions: false,
      selectAllEmails: false,
      navigationOptions: [],
      permissions: [defaultPermission],
      emails: [],
      expirations: EXPIRATION_TYPE.DOES_NOT_EXPIRE,
      expirationMinutes: null,
      expirationDays: null,
    }
  }, [primaryGroupTypes])

  const getUserRoleInfo = async () => {
    if (id) {
      try {
        const primaryGroupTypeAPI = fetchPrimaryGroupType()

        const permissionAPI = fetchPermission()

        const [primaryGroupTypeRes, permissionRes] = await Promise.all([primaryGroupTypeAPI, permissionAPI])
        if (primaryGroupTypeRes?.data) {
          dispatch(setUpPrimaryGroupTypes(primaryGroupTypeRes?.data))
          setDefaultGroupType(
            primaryGroupTypeRes?.data?.filter((item: any) => item.codeName === DEFAULT_GROUP_TYPE)?.[0]?.id,
          )
        }
        if (permissionRes?.data) {
          dispatch(setUpPermissions(permissionRes?.data))
          setDefaultPermission(
            permissionRes?.data?.filter((item: any) => item.codeName === DEFAULT_VIEW_ITEMS)?.[0]?.id,
          )
        }

        const res = await getUserRoleById(id)

        if (res?.data) {
          return {
            primaryGroupType: res?.data?.primaryGroupTypeId,
            roleName: res?.data?.userRoleName?.trim(),
            selectAllNavigations: res?.data?.navigationOptionIdResults?.length === getActiveAll(modules).length,
            navigationOptions: res?.data?.navigationOptionIdResults || [],
            selectAllPermissions: res?.data?.permissionIdResults?.length === permissionRes?.data?.length,
            permissions: res?.data?.permissionIdResults || [
              permissionRes?.data?.filter((item: any) => item.codeName === DEFAULT_VIEW_ITEMS)?.[0]?.id,
            ],
            selectAllEmails: res?.data?.userNotificationTypeIdResults?.length === emails?.length,
            emails: res?.data?.userNotificationTypeIdResults || [],
            expirations: res?.data?.expirationType || EXPIRATION_TYPE.DOES_NOT_EXPIRE,
            expirationMinutes:
              res?.data?.expirationType === EXPIRATION_TYPE.EXPIRE_X_MINUTES ? res?.data?.expirationMinutes : null,
            expirationDays:
              res?.data?.expirationType === EXPIRATION_TYPE.EXPIRE_X_DAYS ? res?.data?.expirationDays : null,
          }
        }
      } catch (error) {
        console.log('error', error)
        return defaultValues
      }
    }

    navigate(menuUrls.userRoles)
  }

  const { control, setValue } = useForm<any>({
    mode: 'all',
    defaultValues: async () => await getUserRoleInfo(),
  })

  const currentPrimaryGroupType = useWatch({
    control,
    name: 'primaryGroupType',
  })

  const PermissionsWatch = useWatch({
    control,
    name: 'permissions',
  })

  const EmailsWatch = useWatch({
    control,
    name: 'emails',
  })

  const navigationOptionsWatch = useWatch({
    control,
    name: 'navigationOptions',
  })

  const handleBack = () => {
    navigate(menuUrls.userRoles)
  }

  useEffect(() => {
    if (!currentPrimaryGroupType) return

    Promise.all([
      fetchSubModuleItem(currentPrimaryGroupType !== defaultGroupType),
      fetchUserNotificationType(currentPrimaryGroupType === defaultGroupType ? 'bank' : 'client'),
    ])
      .then(([subModuleItemRes, userNotificationTypeRes]) => {
        if (subModuleItemRes?.data) {
          if (navigationOptionsWatch?.length === getActiveAll(subModuleItemRes?.data).length) {
            setValue('selectAllNavigations', true)
          }
          dispatch(setUpModules(subModuleItemRes?.data))
        }

        if (userNotificationTypeRes?.data) {
          if (EmailsWatch?.length === userNotificationTypeRes?.data?.length) {
            setValue('selectAllEmails', true)
          }
          dispatch(setUpEmails(userNotificationTypeRes?.data))
        }
      })
      .catch((err) => {
        console.log('Err >> ', err)
      })
      .finally(() => {
        setIsFirstTimeLoadFinished(true)
      })

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPrimaryGroupType])

  if (!isFirstTimeLoadFinished) {
    return <Skeleton />
  }

  return (
    <Form name='view-user-role-form' className='form-container view-user-role-form read-only' layout='vertical'>
      <>
        <Form.Item label={t('UserRoles:primaryGroupType.title')}>
          <Controller
            disabled
            name='primaryGroupType'
            control={control}
            render={({ field }) => (
              <Select
                {...field}
                options={primaryGroupTypes?.map((item: any) => ({
                  ...item,
                  value: item?.id,
                  label: item?.name,
                }))}
                placeholder={t('UserRoles:primaryGroupType.placeholder')}
                id='ant-select-view-only'
              />
            )}
          />
        </Form.Item>

        <Form.Item label={t('UserRoles:roleName.title')} className='isRequired'>
          <Controller
            disabled
            name='roleName'
            control={control}
            render={({ field }) => (
              <Input
                {...field}
                className='ant-input-view-only'
                placeholder={t('UserRoles:roleName.placeholder')}
                onChange={(e) => {
                  field.onChange(e?.target?.value)
                }}
                maxLength={40}
              />
            )}
          />
        </Form.Item>

        <NavigationOptions control={control} navigationOptionsWatch={navigationOptionsWatch} />
        <Switchers
          listSwitchers={filteredPermissions}
          title={t('UserRoles:permissions')}
          name='permissions'
          control={control}
          data={PermissionsWatch}
          allOptionName='selectAllPermissions'
        />

        <Switchers
          listSwitchers={emails}
          title={t('UserRoles:emails')}
          name='emails'
          control={control}
          data={EmailsWatch}
          allOptionName='selectAllEmails'
        />

        <Expirations control={control} />
        <Flex className='button-group' justify='flex-end' gap={24} style={{ zIndex: 1000 }}>
          <Button className='cancel-button' onClick={handleBack}>
            {t('UserRoles:back')}
          </Button>
        </Flex>
      </>
    </Form>
  )
}

export default ViewUserRole
